import { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, User as UserIcon } from "lucide-react";
import { useMember } from "@/lib/member";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_wp-go-live/artifacts/a5en3pve_Logo.jpg";

const links = [
  { label: "Home", path: "/" },
  { label: "Courses", path: "/courses" },
  { label: "Blogs", path: "/blogs" },
  { label: "Videos", path: "/videos" },
  { label: "Worksheets", path: "/worksheets" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { member, signIn } = useMember();

  const isActive = (path) => {
    if (path.includes("#")) return false;
    return location.pathname === path;
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-[#f7f6fb]/90 backdrop-blur-md border-b border-[#1F2547]/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 group">
          <span className="w-12 h-12 rounded-full bg-white overflow-hidden ring-1 ring-[#1F2547]/10 group-hover:rotate-[-6deg] transition-transform">
            <img src={LOGO_URL} alt="Percept Academy logo" className="w-full h-full object-cover" />
          </span>
          <div className="leading-none">
            <div className="font-display font-black text-xl text-[#1F2547]">Percept Academy</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9F87D8] mt-1">
              Step-up in the right direction
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${
                isActive(l.path)
                  ? "bg-[#1F2547] text-white"
                  : "text-[#1F2547] hover:bg-[#EFE9F7]"
              }`}
            >
              {l.label}
            </NavLink>
          ))}

          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="ml-2 px-4 py-2 rounded-full text-sm font-bold text-[#1F2547] hover:bg-[#EFE9F7]"
          >
            Contact us
          </Link>

          {member ? (
            <Link
              to="/my-account"
              data-testid="nav-my-account"
              className="ml-3 inline-flex items-center gap-2 px-4 py-2 bg-[#EFE9F7] text-[#1F2547] font-bold rounded-full btn-press"
            >
              {member.picture ? (
                <img
                  src={member.picture}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <UserIcon className="w-4 h-4" />
              )}
              {member.name?.split(" ")[0] || "Account"}
            </Link>
          ) : (
            <button
              type="button"
              onClick={signIn}
              data-testid="nav-signin"
              className="ml-3 px-5 py-2.5 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow-sm btn-press"
            >
              Sign in / Join
            </button>
          )}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl brutal-border brutal-shadow-sm bg-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu-panel" className="md:hidden border-t border-[#1F2547]/10 bg-[#f7f6fb]">
          <div className="px-6 py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className="px-4 py-3 rounded-xl font-bold text-[#1F2547] hover:bg-[#EFE9F7]"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="px-4 py-3 rounded-xl font-bold text-[#1F2547] hover:bg-[#EFE9F7]"
            >
              Contact us
            </Link>
            {member ? (
              <Link
                to="/my-account"
                onClick={() => setOpen(false)}
                className="px-4 py-3 rounded-xl font-bold text-white bg-[#9F87D8]"
              >
                My Account
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  signIn();
                }}
                className="px-4 py-3 rounded-xl font-bold text-white bg-[#9F87D8] text-left"
              >
                Sign in / Join
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
